import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ELASTIC_TABLES, PROPOSITION_TYPES } from '../../constants/common';
import { splitReferenceSuffix } from '../../utils/sourcesHelper';

const ReferenceList = ({
	title = '',
	references = [],
	collapsible = false,
	defaultCollapsed = true,
	className = '',
	print = false,
	notationIndex = null,
	elasticDocuments = [],
}) => {
	const [expanded, setExpanded] = useState(!defaultCollapsed);
	const [expandedLinks, setExpandedLinks] = useState(new Set());
	const { t } = useTranslation();
	const filteredElasticDocuments =
		elasticDocuments && elasticDocuments.length > 0
			? elasticDocuments.filter((document) =>
					references.some(
						(reference) =>
							reference.id === document.id &&
							reference.table === document.table &&
							reference.type === document.type,
					),
				)
			: null;

	const toggleLinkExpansion = (id) => {
		setExpandedLinks((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				newSet.add(id);
			}
			return newSet;
		});
	};

	const getDocumentIconClass = (document) => {
		if (!document) return 'ri-links-line';

		const { table, type } = document;

		// Map API data shape (table/type) → document categories
		switch (table) {
			// Rättsfall
			case ELASTIC_TABLES.LegalCaseContent:
				return 'ri-auction-line';
			// Lagar
			case ELASTIC_TABLES.LawContent:
				return 'ri-book-2-line';
			// Lagkommentarer
			case ELASTIC_TABLES.LawCommentContent:
				return 'ri-chat-1-line';
			// Föreskrifter & råd
			case ELASTIC_TABLES.RegulationContent:
				return 'ri-file-settings-line';
			// Förarbeten, propositioner och utdrag ur propositioner
			case ELASTIC_TABLES.PropositionContent: {
				if (type === PROPOSITION_TYPES.PROP) {
					// Propositioner
					return 'ri-draft-line';
				}
				if (type === PROPOSITION_TYPES.PROP_EXCERPT) {
					// Propositionsutdrag
					return 'ri-file-text-line';
				}
				// Fallback for other proposition types
				return 'ri-draft-line';
			}
			// Frågor & analyser / analyserande innehåll
			case ELASTIC_TABLES.AnalysisContent:
				return 'ri-question-line';
			default:
				return 'ri-links-line';
		}
	};

	const getDocumentIconTooltip = (document) => {
		if (!document) return '';

		const { table, type } = document;

		switch (table) {
			case ELASTIC_TABLES.LegalCaseContent:
				return t('document_types.legal_case');
			case ELASTIC_TABLES.LawContent:
				return t('document_types.law');
			case ELASTIC_TABLES.LawCommentContent:
				return t('document_types.law_comment');
			case ELASTIC_TABLES.RegulationContent:
				return t('document_types.regulation');
			case ELASTIC_TABLES.PropositionContent: {
				if (type === PROPOSITION_TYPES.PROP_EXCERPT) {
					return t('document_types.proposition_excerpt');
				}
				if (type === PROPOSITION_TYPES.PROP) {
					return t('document_types.proposition');
				}
				return t('document_types.preparatory_work');
			}
			case ELASTIC_TABLES.AnalysisContent:
				return t('document_types.analysis');
			default:
				return '';
		}
	};

	const chunkHasHighlight = (chunk, subChunks) => {
		if (!subChunks || subChunks.length === 0 || !chunk || typeof chunk !== 'string') {
			return false;
		}

		if (subChunks.some((subChunk) => chunk.includes(subChunk))) {
			return true;
		}

		return false;
	};

	const highlightSubChunks = (chunk, subChunks) => {
		if (!subChunks || subChunks.length === 0 || !chunk || typeof chunk !== 'string') {
			return chunk;
		}

		return subChunks
			.map((subChunk) =>
				chunk.replace(subChunk, `<mark class="bg-transparent text-emerald-600">${subChunk}</mark>`),
			)
			.join('')
			.replaceAll('\n', '<br />');
	};
	
	if (!references.length) return null;

	return (
		<div className={`flex flex-col gap-3 ${className}`}>
			<div className='flex items-center justify-between'>
				<p className='text-on-secondary uppercase text-xs font-medium leading-[1.2]'>{title}</p>

				{collapsible && (
					<button
						type='button'
						onClick={() => setExpanded((v) => !v)}
						className='text-primary underline underline-offset-4 hover:no-underline text-sm'
						aria-expanded={expanded}
					>
						{expanded ? t('general.show_less') : t('general.show_more')}
					</button>
				)}
			</div>

			{(!collapsible || expanded) && (
				<div className='flex flex-col gap-3'>
					{!print &&
						references
							.sort((a, b) => notationIndex.numberById(a.id) - notationIndex.numberById(b.id))
							.map(({ id, text, link }, index) => {
								const document = filteredElasticDocuments?.find((doc) => doc.id === id);
								const documentHasHighlight = false; // TODO: Replace with: document?.highlight?.subChunks?.length > 0;
								const documentChunks = document?.chunks || [];
								const documentHighlightSubChunks = document?.highlight?.subChunks || [];
								const chunksWithHighlight = documentChunks.filter((chunk) => {
									return chunkHasHighlight(chunk, documentHighlightSubChunks);
								});

								const chunkIndex = chunksWithHighlight.map((chunk) => {
									return {
										chunk,
										highlight: documentHighlightSubChunks.filter((subChunk) =>
											chunk.includes(subChunk),
										),
									};
								});

								const isLinkExpanded = expandedLinks.has(id);
								const iconClass = getDocumentIconClass(document);
								const iconTooltip = getDocumentIconTooltip(document);
								const { main, suffix } = splitReferenceSuffix(text);

								return (
									<div key={index} className='flex flex-col'>
										<div className='rounded-md border-tertiary bg-on-primary border hover:bg-tertiary-hover group flex items-center gap-2'>
											<a
												href={link}
												target='_blank'
												rel='noopener noreferrer'
												className='flex items-center gap-2 px-[10px] py-[5px] flex-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-on-tertiary-active'
											>
												<i
													className={`${iconClass} text-2xl`}
													style={{ color: '#0e90d2' }}
													title={iconTooltip}
													aria-label={iconTooltip}
												/>
												<span className='group-hover:underline decoration-[1px] decoration-on-tertiary-active flex min-w-0 flex-1 items-baseline gap-1 overflow-hidden text-sm font-medium leading-[1.4] tracking-wide text-on-tertiary-active'>
													<span className='truncate'>
														[{notationIndex.numberById(id)}] {main}
													</span>
													{suffix ? <span className='shrink-0'>{suffix}</span> : null}
												</span>
											</a>
											{documentHasHighlight && (
												<button
													type='button'
													onClick={() => toggleLinkExpansion(id)}
													className='px-2 py-2.5 text-on-tertiary-active cursor-pointer rounded-md hover:text-on-tertiary-active transition-transform duration-200'
													aria-expanded={isLinkExpanded}
												>
													<i
														className={`ri-arrow-down-s-line text-xl transition-transform duration-200 ${
															isLinkExpanded ? 'rotate-180' : ''
														}`}
													/>
												</button>
											)}
										</div>
										{isLinkExpanded && documentHasHighlight && (
											<div className='mt-2 ml-3.5 flex flex-col gap-2'>
												{chunkIndex.map((chunkIndexItem, index) => (
													<div
														key={index}
														className='text-on-tertiary-active text-sm font-normal leading-[1.4] tracking-wide px-[10px] py-[5px] rounded-md bg-gray-50 border border-gray-200'
														dangerouslySetInnerHTML={{
															__html: highlightSubChunks(
																chunkIndexItem.chunk,
																chunkIndexItem.highlight,
															),
														}}
													/>
												))}
											</div>
										)}
									</div>
								);
							})}
				</div>
			)}
		</div>
	);
};

ReferenceList.propTypes = {
	references: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
	).isRequired,
	title: PropTypes.string,
	collapsible: PropTypes.bool,
	defaultCollapsed: PropTypes.bool,
	className: PropTypes.string,
	print: PropTypes.bool,
	notationIndex: PropTypes.object,
	elasticDocuments: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			title: PropTypes.string,
			table: PropTypes.string,
			type: PropTypes.string,
			highlight: PropTypes.shape({
				subChunks: PropTypes.arrayOf(PropTypes.string),
			}),
			updatedAt: PropTypes.string,
			createdAt: PropTypes.string,
			chunks: PropTypes.arrayOf(PropTypes.string),
		}),
	),
};

export default ReferenceList;
